/**
 * Internal custom events (`event/send` → `event/receive` with no event `id`)
 * for the native compiler, scoped to the sender's FlowGraphContext.
 *
 * Why not Babylon's FlowGraphSend/ReceiveCustomEventBlock as-is: those key the
 * coordinator-wide observable by a fixed event id string. That is right for
 * events that link to an external environment, but an INTERNAL event is
 * private to its graph, and the native compiler runs a subgraph as one inner
 * FlowGraph with a fresh context per invocation. A fixed id would cross-talk
 * between invocations (invocation A's send wakes invocation B's receive), and
 * receive blocks in a lazily created inner context never subscribe at all.
 * Keying the event by context gives every invocation its own event, which is
 * exactly what `inlineSubgraphs` produces at export (one copy per invocation).
 *
 * Delivery: DEFERRED to the next frame through the coordinator's own queue
 * (`notifyCustomEvent(id, data, true)`, drained on the next
 * onBeforeRenderObservable). That is what Babylon's KHR_interactivity loader
 * does for every custom event (it sets `dispatchEventsSynchronously = false`),
 * so a graph behaves the same in `.trace` playback and in a strict viewer
 * running its exported GLB — including the per-frame cap
 * (`FlowGraphCoordinator.MaxEventTypeExecutionPerFrame`). The spec leaves
 * delivery timing to the application and calls send/receive loops
 * "asynchronous loops"; deferral also means such a loop can never recurse
 * within a frame.
 */

import { FlowGraphExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlock";
import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";

/** Context variable holding the event ids this context already subscribed. */
const SUBSCRIBED_KEY = 'traceInternalEventSubscriptions';

/** `event/receive` for an internal event: an entry node its event activates. */
export class TraceInternalEventReceiveBlock extends FlowGraphExecutionBlock {
    public readonly out: FlowGraphSignalConnection;

    constructor() {
        super();
        this.out = this._registerSignalOutput("out");
    }

    public override _execute(context: FlowGraphContext, _callingSignal?: FlowGraphSignalConnection): void {
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceInternalEventReceiveBlock";
    }
}

/** `event/send` for an internal event: queue the event for the next frame, then continue. */
export class TraceInternalEventSendBlock extends FlowGraphExecutionBlockWithOutSignal {
    /** Receivers of this event in node order — filled once every block exists. */
    public receivers: TraceInternalEventReceiveBlock[] = [];

    /**
     * @param eventKey Identifies the event within its graph definition (the
     *   graph and event index); the context id is added per execution.
     */
    constructor(private readonly _eventKey: string) {
        super();
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const coordinator = context.configuration.coordinator;
        const id = `__trace_internal_${context.uniqueId}_${this._eventKey}`;
        const subscribed = context._getGlobalContextVariable<Set<string>>(SUBSCRIBED_KEY, new Set());
        if (!subscribed.has(id)) {
            // Once per (context, event): every sender of the event shares the
            // same receivers, so one subscription delivers each send once.
            const receivers = this.receivers;
            coordinator.getCustomEventObservable(id).add(() => {
                for (const receiver of receivers) receiver._execute(context);
            });
            subscribed.add(id);
            context._setGlobalContextVariable(SUBSCRIBED_KEY, subscribed);
        }
        coordinator.notifyCustomEvent(id, {}, true);
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceInternalEventSendBlock";
    }
}
